// Login page. Email/password + Google. Redirects to /teams on success.

import { GoogleAuthProvider, signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth'
import { useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '../../components/ui/button.jsx'
import { auth } from '../../lib/firebase.js'

export function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const [params] = useSearchParams()
  const returnToParam = params.get('returnTo')
  const returnTo = returnToParam ?? location.state?.from ?? '/teams'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleEmailLogin(e) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await signInWithEmailAndPassword(auth, email, password)
      navigate(returnTo, { replace: true })
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleGoogle() {
    setError(null)
    setSubmitting(true)
    try {
      await signInWithPopup(auth, new GoogleAuthProvider())
      navigate(returnTo, { replace: true })
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main id="main-content" className="mx-auto max-w-md px-6 py-16">
      <h1 className="mb-2 text-2xl font-semibold text-[var(--color-basalt)]">Sign in</h1>
      <p className="mb-8 text-sm text-[var(--color-schist)]">
        Welcome back. Use your email and password, or continue with Google.
      </p>

      <form onSubmit={handleEmailLogin} className="space-y-4">
        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-[var(--color-schist)]">
            Email
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            className="w-full rounded-md border border-[var(--color-sandstone)] bg-white px-3 py-2 text-sm focus:outline-none focus-visible:border-[var(--color-terracotta)]"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-[var(--color-schist)]">
            Password
          </span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            className="w-full rounded-md border border-[var(--color-sandstone)] bg-white px-3 py-2 text-sm focus:outline-none focus-visible:border-[var(--color-terracotta)]"
          />
        </label>

        {error && (
          <p className="text-sm text-[var(--color-failed)]" role="alert">
            {error}
          </p>
        )}

        <Button type="submit" disabled={submitting} variant="primary" className="w-full">
          {submitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>

      <div className="my-6 flex items-center gap-3">
        <hr className="flex-1 border-[var(--color-sandstone)]" />
        <span className="text-xs uppercase tracking-wider text-[var(--color-schist)]">or</span>
        <hr className="flex-1 border-[var(--color-sandstone)]" />
      </div>

      <Button onClick={handleGoogle} variant="outline" disabled={submitting} className="w-full">
        Continue with Google
      </Button>

      <div className="mt-8 space-y-2 text-sm text-[var(--color-schist)]">
        <p>
          <Link
            to="/auth/forgot-password"
            className="text-[var(--color-terracotta)] underline-offset-4 hover:underline"
          >
            Forgot password
          </Link>
        </p>
        <p>
          New to Cairn?{' '}
          <Link
            to="/auth/signup"
            className="text-[var(--color-terracotta)] underline-offset-4 hover:underline"
          >
            Create an account
          </Link>
        </p>
      </div>
    </main>
  )
}
